import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Slider } from '../ui/slider';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import Pagination from '../common/Pagination';
import StagingActionsHelpPopover from './StagingActionsHelpPopover';
import {
  Loader2, CheckCircle2, GitMerge, SkipForward, Clock, RefreshCw,
  Zap, ListFilter, AlertTriangle, Tag, UserCheck, Plus, Settings2, Eye,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const ROWS_PER_PAGE = 10;

/**
 * Bulk Similarity Modal
 *
 * Lets the admin review every staging question in a batch against approved-DB
 * and other-staging matches in one pass. Re-uses the new
 * /admin/bulk-import/batch/{id}/bulk-similarity endpoint.
 *
 * Per-row actions:
 *   keep  — leave staging row untouched (admin will approve via normal flow)
 *   merge — call /admin/questions/merge with the selected target on Apply
 *   skip  — delete the staging row on Apply (bulk-delete)
 *   defer — UI-only bookmark, no DB effect
 *
 * Bulk action: apply the same action to every row in a Keyword group.
 */
const BulkSimilarityModal = ({ open, onClose, batchId, scope, onApplied }) => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [threshold, setThreshold] = useState(70);
  const [keywordFilter, setKeywordFilter] = useState(null);
  const [rowState, setRowState] = useState({});      // staging_id -> { action, mergeTargetId }
  const [page, setPage] = useState(1);
  const [applying, setApplying] = useState(false);

  // Phase 3 power-up dropdowns (scope-aware topic + surveyor pools)
  const [topics, setTopics] = useState([]);
  const [scopeSurveyors, setScopeSurveyors] = useState([]);
  const [powerupBusy, setPowerupBusy] = useState(false);

  // Local form state for each power-up popover.
  // Defaults are factored out so we can reset to a clean slate after apply
  // OR after the popover is dismissed without applying.
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];
  const buildDefaults = useCallback(() => {
    const now = new Date();
    return {
      topic: { topic_ids: [] },
      surveyor: { asked_by_internal: false, surveyor_id: '' },
      sitting: {
        asked_by_internal: false,
        surveyor_id: '',
        exam_month: MONTHS[now.getMonth()],
        exam_year: String(now.getFullYear()),
        asked: 1,
      },
      count: { asked: 1 },
    };
  }, []); // MONTHS is module-level constant; safe to omit
  const _defaults = useMemo(() => buildDefaults(), [buildDefaults]);

  const [topicForm, setTopicForm] = useState(_defaults.topic);
  const [surveyorForm, setSurveyorForm] = useState(_defaults.surveyor);
  const [sittingForm, setSittingForm] = useState(_defaults.sitting);
  const [countForm, setCountForm] = useState(_defaults.count);

  const resetTopicForm = useCallback(() => setTopicForm(buildDefaults().topic), [buildDefaults]);
  const resetSurveyorForm = useCallback(() => setSurveyorForm(buildDefaults().surveyor), [buildDefaults]);
  const resetSittingForm = useCallback(() => setSittingForm(buildDefaults().sitting), [buildDefaults]);
  const resetCountForm = useCallback(() => setCountForm(buildDefaults().count), [buildDefaults]);

  // Popover open states
  const [topicPopoverOpen, setTopicPopoverOpen] = useState(false);
  const [surveyorPopoverOpen, setSurveyorPopoverOpen] = useState(false);
  const [sittingPopoverOpen, setSittingPopoverOpen] = useState(false);
  const [countPopoverOpen, setCountPopoverOpen] = useState(false);

  // Fetch the batched similarity data when modal opens
  const fetchData = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    try {
      // No threshold param = backend uses admin/tools default
      const res = await axios.post(
        `${API}/admin/bulk-import/batch/${batchId}/bulk-similarity?top_k=5`,
        null,
        { headers }
      );
      setPayload(res.data);
      // Init slider to whatever threshold the backend used
      setThreshold(res.data?.threshold_used ?? 70);
      // Init each row's action to 'keep'
      const init = {};
      Object.keys(res.data?.results || {}).forEach((sid) => {
        init[sid] = { action: 'keep', mergeTargetId: null, mergeTargetSource: null };
      });
      setRowState(init);
      setPage(1);
      setKeywordFilter(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to compute bulk similarity');
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    if (open) fetchData();
    else {
      setPayload(null);
      setRowState({});
      // Wipe any half-filled power-up form so reopening starts clean.
      resetTopicForm();
      resetSurveyorForm();
      resetSittingForm();
      resetCountForm();
    }
  }, [open, fetchData, resetTopicForm, resetSurveyorForm, resetSittingForm, resetCountForm]);

  // Fetch topics + surveyors for this batch scope (used by power-up popovers)
  useEffect(() => {
    if (!open || !scope) return;
    const { grade, mmd_location, function_id } = scope || {};
    if (!grade || !function_id) return;

    const NAUTICAL = ['Master', 'Chief Mate', '2MFG'];
    const isNautical = NAUTICAL.includes(grade);

    const fetchScopedRefs = async () => {
      try {
        const [tRes, sRes] = await Promise.all([
          axios.get(`${API}/topics`, {
            headers,
            params: { grade, function_id },
          }),
          axios.get(`${API}/surveyors`, { headers }),
        ]);
        setTopics(tRes.data || []);
        // Client-side filter: grade-match + (Nautical => any MMD, else scope MMD)
        const filtered = (sRes.data || []).filter((s) => {
          if (s.is_active === false) return false;
          const hasGrade = s.grades?.includes(grade);
          if (!hasGrade) return false;
          if (isNautical) return true;
          const mmdAlt = mmd_location?.endsWith(' MMD') ? mmd_location : `${mmd_location} MMD`;
          return s.mmd_locations?.includes(mmd_location) || s.mmd_locations?.includes(mmdAlt);
        });
        setScopeSurveyors(filtered);
      } catch (err) {
        // Non-blocking — power-ups stay disabled if refs fail to load
        console.warn('Failed to load topics/surveyors for power-ups', err);
      }
    };
    fetchScopedRefs();
  }, [open, scope?.grade, scope?.mmd_location, scope?.function_id]);

  // ---------- Derived data: client-side threshold + keyword filter ----------
  const allRows = useMemo(() => Object.values(payload?.results || {}), [payload]);

  const allKeywords = useMemo(() => {
    const set = new Map();  // tag -> count
    for (const r of allRows) {
      const tags = (r.keywords || '').split(';').map(t => t.trim()).filter(Boolean);
      for (const t of tags) set.set(t, (set.get(t) || 0) + 1);
    }
    return Array.from(set.entries()).sort((a, b) => b[1] - a[1]);
  }, [allRows]);

  const filteredRows = useMemo(() => {
    let rows = allRows.map(r => ({
      ...r,
      approved_similar_filtered: r.approved_similar.filter(c => c.similarity >= threshold),
      staging_similar_filtered: r.staging_similar.filter(c => c.similarity >= threshold),
    }));
    if (keywordFilter) {
      rows = rows.filter(r => {
        const tags = (r.keywords || '').split(';').map(t => t.trim());
        return tags.includes(keywordFilter);
      });
    }
    return rows;
  }, [allRows, threshold, keywordFilter]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE;
    return filteredRows.slice(start, start + ROWS_PER_PAGE);
  }, [filteredRows, page]);

  // ---------- Action manipulation ----------
  const setAction = (stagingId, action, mergeTargetId = null, mergeTargetSource = null) => {
    setRowState(prev => ({ ...prev, [stagingId]: { action, mergeTargetId, mergeTargetSource } }));
  };

  const applyToGroup = (action) => {
    // Apply same action to every row in current filter (keyword group)
    setRowState(prev => {
      const next = { ...prev };
      for (const r of filteredRows) {
        next[r.staging_question_id] = { action, mergeTargetId: null, mergeTargetSource: null };
      }
      return next;
    });
    toast.info(`Set ${filteredRows.length} row(s) to "${action}"`);
  };

  // -------- Phase 3 Power-Ups (bulk topic / surveyor / asked-count) ----------
  const targetIds = useMemo(
    () => filteredRows.map((r) => r.staging_question_id),
    [filteredRows]
  );

  const callBulkEdit = async (extraBody) => {
    if (targetIds.length === 0) {
      toast.error('No staging rows in current filter');
      return false;
    }
    setPowerupBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/bulk-import/staging/bulk-edit`,
        { staging_ids: targetIds, ...extraBody },
        { headers }
      );
      toast.success(res.data?.message || `Updated ${targetIds.length} row(s)`);
      // Refresh similarity payload so user sees fresh row data
      await fetchData();
      onApplied?.();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk edit failed');
      return false;
    } finally {
      setPowerupBusy(false);
    }
  };

  const handleApplyTopics = async () => {
    const ok = await callBulkEdit({ topic_ids: topicForm.topic_ids });
    if (ok) {
      setTopicPopoverOpen(false);
      resetTopicForm();
    }
  };

  const handleApplySurveyor = async () => {
    if (!surveyorForm.surveyor_id) {
      toast.error('Pick a surveyor first');
      return;
    }
    const body = { asked_by_internal: surveyorForm.asked_by_internal };
    if (surveyorForm.asked_by_internal) body.surveyor_internal_id = surveyorForm.surveyor_id;
    else body.surveyor_external_id = surveyorForm.surveyor_id;
    const ok = await callBulkEdit(body);
    if (ok) {
      setSurveyorPopoverOpen(false);
      resetSurveyorForm();
    }
  };

  const handleApplySitting = async () => {
    if (!sittingForm.surveyor_id) {
      toast.error('Pick a surveyor first');
      return;
    }
    const entry = {
      surveyor_id: sittingForm.surveyor_id,
      surveyor_type: sittingForm.asked_by_internal ? 'internal' : 'external',
      exam_month: `${sittingForm.exam_month} ${sittingForm.exam_year}`,
      asked: Math.max(1, parseInt(sittingForm.asked, 10) || 1),
    };
    const ok = await callBulkEdit({ asked_history_entry: entry });
    if (ok) {
      setSittingPopoverOpen(false);
      resetSittingForm();
    }
  };

  const handleApplyCount = async () => {
    const n = parseInt(countForm.asked, 10);
    if (Number.isNaN(n) || n < 0) {
      toast.error('Asked count must be a non-negative integer');
      return;
    }
    const ok = await callBulkEdit({ asked_count_set: n });
    if (ok) {
      setCountPopoverOpen(false);
      resetCountForm();
    }
  };

  // Filtered surveyor pools for popovers
  const internalSurveyors = useMemo(
    () => scopeSurveyors.filter((s) => s.type === 'internal'),
    [scopeSurveyors]
  );
  const externalSurveyors = useMemo(
    () => scopeSurveyors.filter((s) => s.type === 'external'),
    [scopeSurveyors]
  );

  // ---------- Summary ----------
  const summary = useMemo(() => {
    let keep = 0, merge = 0, skip = 0, defer = 0;
    for (const sid in rowState) {
      const a = rowState[sid].action;
      if (a === 'keep') keep++;
      else if (a === 'merge') merge++;
      else if (a === 'skip') skip++;
      else if (a === 'defer') defer++;
    }
    return { keep, merge, skip, defer };
  }, [rowState]);

  // ---------- Apply ----------
  const handleApply = async () => {
    const skipIds = [];
    const approvedMergeOps = [];  // merge staging → approved DB question
    const stagingMergeOps = [];   // merge staging → another staging row
    for (const sid in rowState) {
      const { action, mergeTargetId, mergeTargetSource } = rowState[sid];
      if (action === 'skip') skipIds.push(sid);
      else if (action === 'merge' && mergeTargetId) {
        if (mergeTargetSource === 'staging') {
          stagingMergeOps.push({ source_staging_id: sid, target_staging_id: mergeTargetId });
        } else {
          approvedMergeOps.push({ staging_id: sid, target_id: mergeTargetId });
        }
      }
    }
    if (skipIds.length === 0 && approvedMergeOps.length === 0 && stagingMergeOps.length === 0) {
      toast.error('No actions to apply (only Delete/Merge have DB effect — Keep/Defer are no-ops).');
      return;
    }
    setApplying(true);
    let okCount = 0, errCount = 0;
    try {
      // 1) Skips → one bulk-delete
      if (skipIds.length) {
        try {
          await axios.post(
            `${API}/admin/bulk-import/staging/bulk-delete`,
            { staging_ids: skipIds },
            { headers }
          );
          okCount += skipIds.length;
        } catch (e) {
          errCount += skipIds.length;
        }
      }
      // 2a) Merges into approved DB → per-row (sequential to keep history consistent)
      for (const { staging_id, target_id } of approvedMergeOps) {
        try {
          // NOTE: matches the payload shape of MergeQuestionRequest
          // (source_question_id + source_type='staging'). See the same
          // comment in BulkSimilarityPage.jsx executeApply. This file is
          // dormant (not mounted); keeping the payload correct so the
          // code path doesn't silently rot.
          await axios.post(`${API}/admin/questions/merge`, {
            source_question_id: staging_id,
            target_question_id: target_id,
            source_type: 'staging',
            update_answer: false,
            merge_pictures: false,
          }, { headers });
          okCount++;
        } catch (e) {
          errCount++;
        }
      }
      // 2b) Merges into another staging row → per-row (sequential)
      for (const { source_staging_id, target_staging_id } of stagingMergeOps) {
        try {
          await axios.post(`${API}/admin/bulk-import/staging/merge-into-staging`, {
            source_staging_id,
            target_staging_id,
          }, { headers });
          okCount++;
        } catch (e) {
          errCount++;
        }
      }
      toast.success(`Applied ${okCount} action(s)${errCount ? `, ${errCount} failed` : ''}.`);
      if (onApplied) onApplied();
      onClose?.();
    } finally {
      setApplying(false);
    }
  };

  // ---------- Render helpers ----------
  const renderMatchCard = (m, stagingId, opts = {}) => {
    // Staging matches in the SAME batch are intentionally not mergeable here —
    // within-batch dedupe runs at upload time. Cross-file staging merges and
    // approved-DB merges are both supported.
    const isMergeable = m.source === 'approved' || (m.source === 'staging' && !m.is_same_batch);
    const targetSource = m.source === 'approved' ? 'approved' : 'staging';
    const isSelected =
      rowState[stagingId]?.action === 'merge' &&
      rowState[stagingId]?.mergeTargetId === m.id;
    return (
      <div
        key={`${m.source}-${m.id}`}
        className={`p-2 border rounded text-xs ${isSelected ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
        data-testid={`sim-match-${m.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <span className="text-gray-800 line-clamp-2 flex-1">{m.question_text}</span>
          <div className="flex items-center gap-1 shrink-0">
            {/* Preview popover — shows full text + metadata before commit */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="p-0.5 text-gray-400 hover:text-indigo-600 rounded"
                  title="Preview full question & sittings"
                  data-testid={`preview-match-${m.id}`}
                  aria-label="Preview"
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] text-xs" align="end" side="left">
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-sm text-gray-800">
                      {m.source === 'approved' ? 'Approved DB question' : 'Staging question'}
                    </div>
                    <Badge variant="outline" className={`shrink-0 ${m.similarity >= 90 ? 'bg-red-50 text-red-700 border-red-200' : m.similarity >= 75 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-50 text-gray-600'}`}>
                      {m.similarity}% match
                    </Badge>
                  </div>

                  {/* Full question text */}
                  <div className="border rounded p-2 bg-gray-50 text-gray-800 max-h-32 overflow-y-auto whitespace-pre-wrap">
                    {m.question_text}
                  </div>

                  {/* Metadata grid */}
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                    <div><span className="text-gray-500">Grade:</span> <span className="text-gray-800 font-medium">{m.grade || '—'}</span></div>
                    <div><span className="text-gray-500">MMD:</span> <span className="text-gray-800 font-medium">{m.mmd_location || '—'}</span></div>
                    <div className="col-span-2"><span className="text-gray-500">Function:</span> <span className="text-gray-800 font-medium">{m.function_name || '—'}</span></div>
                    <div><span className="text-gray-500">Surveyor:</span> <span className="text-gray-800 font-medium">{m.surveyor_name || '—'}</span></div>
                    <div><span className="text-gray-500">Exam:</span> <span className="text-gray-800 font-medium">{m.exam_month || '—'}</span></div>
                    <div><span className="text-gray-500">Asked count:</span> <span className="text-gray-800 font-medium">{m.asked ?? '—'}</span></div>
                    <div><span className="text-gray-500">Sittings logged:</span> <span className="text-gray-800 font-medium">{m.history_count ?? 0}</span></div>
                  </div>

                  {/* Keywords */}
                  {m.keywords && (
                    <div>
                      <div className="text-[10px] text-gray-500 mb-0.5">Keywords</div>
                      <div className="flex flex-wrap gap-1">
                        {String(m.keywords).split(';').map(t => t.trim()).filter(Boolean).map(t => (
                          <Badge key={t} variant="outline" className="text-[9px] text-indigo-700 border-indigo-200 bg-indigo-50">
                            {t}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent sittings */}
                  {m.history_recent && m.history_recent.length > 0 && (
                    <div>
                      <div className="text-[10px] text-gray-500 mb-1">
                        Recent sittings {m.history_count > m.history_recent.length ? `(${m.history_recent.length} of ${m.history_count})` : ''}
                      </div>
                      <ul className="space-y-0.5 text-[11px]">
                        {m.history_recent.map((h, i) => (
                          <li key={i} className="flex items-center gap-2 text-gray-700">
                            <span className="text-gray-400">•</span>
                            <span className="font-medium">{h.surveyor_name || '—'}</span>
                            <span className="text-gray-500">·</span>
                            <span>{h.exam_month || '—'}</span>
                            {h.asked > 1 && <span className="text-gray-400">×{h.asked}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Source meta */}
                  {m.source === 'staging' && (
                    <div className="text-[10px] text-gray-400 pt-1 border-t">
                      From {m.source_filename || '—'} · Q{m.question_number || '?'} · Row {m.row_number}
                      {m.is_same_batch && ' (this batch)'}
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <Badge variant="outline" className={`${m.similarity >= 90 ? 'bg-red-50 text-red-700 border-red-200' : m.similarity >= 75 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-50 text-gray-600'}`}>
              {m.similarity}%
            </Badge>
          </div>
        </div>
        <div className="flex items-center justify-between mt-1 text-[10px] text-gray-500">
          <span>
            {m.source === 'approved'
              ? `Approved · ${m.surveyor_name} · ${m.exam_month || '—'}`
              : `Staging Q${m.question_number || '?'} · ${m.source_filename || '—'}${m.is_same_batch ? ' (this batch)' : ''}`}
          </span>
          {isMergeable && (
            <button
              onClick={() => setAction(stagingId, 'merge', m.id, targetSource)}
              className={`text-[10px] hover:underline ${targetSource === 'staging' ? 'text-indigo-700' : 'text-emerald-700'}`}
              data-testid={`pick-merge-${stagingId}-${m.id}`}
            >
              {isSelected
                ? '✓ Merge target'
                : targetSource === 'staging'
                  ? 'Merge into this'
                  : 'Merge with existing'}
            </button>
          )}
        </div>
      </div>
    );
  };

  // ---------- UI ----------
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!applying && !o) onClose?.(); }}>
      <DialogContent className="max-w-7xl max-h-[95vh] flex flex-col" data-testid="bulk-similarity-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-600" />
            Bulk Similarity Check
            {payload?.elapsed_ms && (
              <Badge variant="outline" className="text-[10px] ml-2">
                computed in {payload.elapsed_ms}ms
              </Badge>
            )}
            {/* Help popover — explains the 4 per-row actions */}
            <StagingActionsHelpPopover testId="bulk-sim-help-btn" align="start" />
          </DialogTitle>
          <DialogDescription className="text-xs">
            One-shot similarity scoring for every staging question in this batch — against approved DB, same-batch siblings, and other admins&apos; staging in the same Grade+MMD+Function. Tune threshold, group by keyword, and decide per-row.
          </DialogDescription>
        </DialogHeader>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400 mr-2" />
            <span className="text-sm text-gray-500">Computing similarities…</span>
          </div>
        )}

        {!loading && payload && (
          <>
            {/* Controls bar */}
            <div className="flex flex-wrap items-center gap-3 px-1 py-2 border-y bg-gray-50">
              <div className="flex items-center gap-2 min-w-[280px]">
                <Label className="text-xs whitespace-nowrap">Threshold: <strong>{threshold}%</strong></Label>
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  value={[threshold]}
                  onValueChange={(v) => setThreshold(v[0])}
                  className="flex-1"
                  data-testid="threshold-slider"
                />
              </div>
              <Button size="sm" variant="outline" onClick={fetchData} disabled={loading}
                className="h-7 text-xs" data-testid="refresh-similarity-btn">
                <RefreshCw className="w-3 h-3 mr-1" /> Recompute
              </Button>

              {/* Keyword filter chips */}
              {allKeywords.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  <ListFilter className="w-3 h-3 text-gray-400" />
                  <Badge
                    onClick={() => setKeywordFilter(null)}
                    className={`cursor-pointer text-[10px] ${!keywordFilter ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    All ({allRows.length})
                  </Badge>
                  {allKeywords.slice(0, 10).map(([kw, n]) => (
                    <Badge
                      key={kw}
                      onClick={() => setKeywordFilter(kw === keywordFilter ? null : kw)}
                      className={`cursor-pointer text-[10px] ${keywordFilter === kw ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      data-testid={`kw-chip-${kw}`}
                    >
                      {kw} · {n}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Bulk actions on current filter */}
              <div className="ml-auto flex items-center gap-1">
                <span className="text-[10px] text-gray-500 mr-1">Set all visible:</span>
                <Button size="sm" variant="outline" onClick={() => applyToGroup('keep')}
                  className="h-7 px-2 text-[10px]" data-testid="group-keep-btn">
                  Keep
                </Button>
                <Button size="sm" variant="outline" onClick={() => applyToGroup('skip')}
                  className="h-7 px-2 text-[10px]" data-testid="group-skip-btn">
                  Delete
                </Button>
                <Button size="sm" variant="outline" onClick={() => applyToGroup('defer')}
                  className="h-7 px-2 text-[10px]" data-testid="group-defer-btn">
                  Defer
                </Button>
              </div>
            </div>

            {/* Phase 3 Power-Ups — bulk mutate filtered group server-side */}
            {scope && (
              <div className="flex flex-wrap items-center gap-2 px-1 py-1.5 border-b bg-indigo-50/40">
                <div className="flex items-center gap-1.5 text-[11px] text-indigo-900 font-medium">
                  <Settings2 className="w-3.5 h-3.5" />
                  Power-Ups
                  <span className="text-[10px] text-indigo-700/70 font-normal">
                    · acts on the {filteredRows.length} filtered row(s)
                  </span>
                </div>

                {/* --- Set Topics --- */}
                <Popover
                  open={topicPopoverOpen}
                  onOpenChange={(o) => {
                    setTopicPopoverOpen(o);
                    if (!o) resetTopicForm();
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px]"
                      disabled={powerupBusy || filteredRows.length === 0}
                      data-testid="powerup-topics-btn"
                    >
                      <Tag className="w-3 h-3 mr-1" /> Set Topics
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 max-h-[380px] overflow-y-auto" align="end">
                    <div className="space-y-2">
                      <Label className="text-xs">Topics for this scope ({topics.length})</Label>
                      <div className="text-[10px] text-gray-500">
                        Pick one or more. Empty selection clears topics.
                      </div>
                      <div className="space-y-1 max-h-[240px] overflow-y-auto border rounded p-1">
                        {topics.length === 0 && (
                          <div className="text-[11px] text-gray-400 italic px-2 py-1">
                            No topics defined for this scope yet.
                          </div>
                        )}
                        {topics.map((t) => {
                          const checked = topicForm.topic_ids.includes(t.id);
                          return (
                            <label
                              key={t.id}
                              className="flex items-center gap-2 text-[11px] px-2 py-1 hover:bg-gray-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setTopicForm((prev) => ({
                                    topic_ids: e.target.checked
                                      ? [...prev.topic_ids, t.id]
                                      : prev.topic_ids.filter((x) => x !== t.id),
                                  }));
                                }}
                                data-testid={`topic-cb-${t.id}`}
                              />
                              <span>{t.name}</span>
                            </label>
                          );
                        })}
                      </div>
                      <div className="flex justify-between items-center pt-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[10px]"
                          onClick={() => setTopicForm({ topic_ids: [] })}
                          data-testid="topic-clear-btn"
                        >
                          Clear all
                        </Button>
                        <Button
                          size="sm"
                          className="h-7 px-3 text-[11px] bg-indigo-600 hover:bg-indigo-700"
                          onClick={handleApplyTopics}
                          disabled={powerupBusy}
                          data-testid="topic-apply-btn"
                        >
                          {powerupBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                          Apply to {filteredRows.length}
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* --- Set Surveyor --- */}
                <Popover
                  open={surveyorPopoverOpen}
                  onOpenChange={(o) => {
                    setSurveyorPopoverOpen(o);
                    if (!o) resetSurveyorForm();
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px]"
                      disabled={powerupBusy || filteredRows.length === 0}
                      data-testid="powerup-surveyor-btn"
                    >
                      <UserCheck className="w-3 h-3 mr-1" /> Change Surveyor
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72" align="end">
                    <div className="space-y-2">
                      <Label className="text-xs">Surveyor Type</Label>
                      <Select
                        value={surveyorForm.asked_by_internal ? 'internal' : 'external'}
                        onValueChange={(v) => setSurveyorForm({ asked_by_internal: v === 'internal', surveyor_id: '' })}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="surveyor-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="external">External</SelectItem>
                          <SelectItem value="internal">Internal</SelectItem>
                        </SelectContent>
                      </Select>

                      <Label className="text-xs">Surveyor</Label>
                      <Select
                        value={surveyorForm.surveyor_id || ''}
                        onValueChange={(v) => setSurveyorForm((prev) => ({ ...prev, surveyor_id: v }))}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="surveyor-select">
                          <SelectValue placeholder="Pick a surveyor..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {(surveyorForm.asked_by_internal ? internalSurveyors : externalSurveyors).map((s) => (
                            <SelectItem key={s.id} value={s.id} data-testid={`surveyor-opt-${s.id}`}>
                              {s.name}
                            </SelectItem>
                          ))}
                          {(surveyorForm.asked_by_internal ? internalSurveyors : externalSurveyors).length === 0 && (
                            <div className="px-2 py-1 text-[11px] text-gray-400 italic">No surveyors</div>
                          )}
                        </SelectContent>
                      </Select>

                      <Button
                        size="sm"
                        className="w-full h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700"
                        onClick={handleApplySurveyor}
                        disabled={powerupBusy || !surveyorForm.surveyor_id}
                        data-testid="surveyor-apply-btn"
                      >
                        {powerupBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                        Apply to {filteredRows.length}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* --- Add Sitting (history entry) --- */}
                <Popover
                  open={sittingPopoverOpen}
                  onOpenChange={(o) => {
                    setSittingPopoverOpen(o);
                    if (!o) resetSittingForm();
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px]"
                      disabled={powerupBusy || filteredRows.length === 0}
                      data-testid="powerup-sitting-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add Sitting
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72" align="end">
                    <div className="space-y-2">
                      <div className="text-[10px] text-gray-500">
                        Appends an asked_by_history entry and bumps the asked count by N.
                      </div>
                      <Label className="text-xs">Surveyor Type</Label>
                      <Select
                        value={sittingForm.asked_by_internal ? 'internal' : 'external'}
                        onValueChange={(v) => setSittingForm((prev) => ({ ...prev, asked_by_internal: v === 'internal', surveyor_id: '' }))}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="sitting-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="external">External</SelectItem>
                          <SelectItem value="internal">Internal</SelectItem>
                        </SelectContent>
                      </Select>

                      <Label className="text-xs">Surveyor</Label>
                      <Select
                        value={sittingForm.surveyor_id || ''}
                        onValueChange={(v) => setSittingForm((prev) => ({ ...prev, surveyor_id: v }))}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="sitting-surveyor-select">
                          <SelectValue placeholder="Pick a surveyor..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {(sittingForm.asked_by_internal ? internalSurveyors : externalSurveyors).map((s) => (
                            <SelectItem key={s.id} value={s.id} data-testid={`sitting-surveyor-opt-${s.id}`}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <div className="grid grid-cols-3 gap-1">
                        <div className="col-span-2">
                          <Label className="text-xs">Month</Label>
                          <Select
                            value={sittingForm.exam_month}
                            onValueChange={(v) => setSittingForm((prev) => ({ ...prev, exam_month: v }))}
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid="sitting-month-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {MONTHS.map((m) => (
                                <SelectItem key={m} value={m}>{m}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Year</Label>
                          <Input
                            type="number"
                            value={sittingForm.exam_year}
                            onChange={(e) => setSittingForm((prev) => ({ ...prev, exam_year: e.target.value }))}
                            className="h-8 text-xs"
                            data-testid="sitting-year-input"
                          />
                        </div>
                      </div>

                      <Label className="text-xs">Count to add (per row)</Label>
                      <Input
                        type="number"
                        min={1}
                        value={sittingForm.asked}
                        onChange={(e) => setSittingForm((prev) => ({ ...prev, asked: e.target.value }))}
                        className="h-8 text-xs"
                        data-testid="sitting-asked-input"
                      />

                      <Button
                        size="sm"
                        className="w-full h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700"
                        onClick={handleApplySitting}
                        disabled={powerupBusy || !sittingForm.surveyor_id}
                        data-testid="sitting-apply-btn"
                      >
                        {powerupBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                        Append to {filteredRows.length}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* --- Set Asked Count (override) --- */}
                <Popover
                  open={countPopoverOpen}
                  onOpenChange={(o) => {
                    setCountPopoverOpen(o);
                    if (!o) resetCountForm();
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px]"
                      disabled={powerupBusy || filteredRows.length === 0}
                      data-testid="powerup-count-btn"
                    >
                      <Settings2 className="w-3 h-3 mr-1" /> Set Asked Count
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64" align="end">
                    <div className="space-y-2">
                      <Label className="text-xs">New asked count (overrides existing)</Label>
                      <div className="text-[10px] text-gray-500">
                        Use this to fix a wrong total. Does not touch asked_by_history.
                      </div>
                      <Input
                        type="number"
                        min={0}
                        value={countForm.asked}
                        onChange={(e) => setCountForm({ asked: e.target.value })}
                        className="h-8 text-xs"
                        data-testid="count-input"
                      />
                      <Button
                        size="sm"
                        className="w-full h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700"
                        onClick={handleApplyCount}
                        disabled={powerupBusy}
                        data-testid="count-apply-btn"
                      >
                        {powerupBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                        Apply to {filteredRows.length}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* Rows */}
            <div className="flex-1 overflow-auto -mx-2 px-2 py-2 space-y-2">
              {pagedRows.length === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No rows in this view. {keywordFilter && 'Try clearing the keyword filter.'}
                </div>
              )}
              {pagedRows.map((r) => {
                const action = rowState[r.staging_question_id]?.action || 'keep';
                const hasMatches = r.approved_similar_filtered.length + r.staging_similar_filtered.length > 0;
                const cardBg = action === 'skip' ? 'bg-red-50/40 border-red-200'
                  : action === 'merge' ? 'bg-emerald-50/40 border-emerald-200'
                  : action === 'defer' ? 'bg-amber-50/40 border-amber-200'
                  : 'bg-white border-gray-200';
                return (
                  <div key={r.staging_question_id} className={`border rounded-lg p-3 ${cardBg}`}
                    data-testid={`bulk-sim-row-${r.staging_question_id}`}>
                    <div className="grid grid-cols-12 gap-3">
                      {/* Left: staging question */}
                      <div className="col-span-5">
                        <div className="text-[10px] text-gray-400 font-mono">Row {r.row_number}</div>
                        <div className="text-sm text-gray-800 whitespace-pre-wrap">{r.question_text}</div>
                        {r.keywords && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {r.keywords.split(';').map(t => t.trim()).filter(Boolean).map(t => (
                              <Badge key={t} variant="outline" className="text-[9px] text-indigo-700 border-indigo-200 bg-indigo-50">
                                {t}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Middle: matches */}
                      <div className="col-span-5 space-y-1.5">
                        {!hasMatches && (
                          <div className="text-[11px] text-gray-400 italic py-1">
                            No matches at {threshold}% threshold
                          </div>
                        )}
                        {r.approved_similar_filtered.map((m) => renderMatchCard(m, r.staging_question_id))}
                        {r.staging_similar_filtered.map((m) => renderMatchCard(m, r.staging_question_id, { sameBatch: m.is_same_batch }))}
                      </div>

                      {/* Right: actions */}
                      <div className="col-span-2 flex flex-col gap-1">
                        {[
                          { v: 'keep', icon: CheckCircle2, label: 'Keep', cls: 'text-gray-700' },
                          { v: 'merge', icon: GitMerge, label: 'Merge', cls: 'text-emerald-700' },
                          { v: 'skip', icon: SkipForward, label: 'Delete', cls: 'text-red-700' },
                          { v: 'defer', icon: Clock, label: 'Defer', cls: 'text-amber-700' },
                        ].map(({ v, icon: Icon, label, cls }) => {
                          const hasApprovedCandidate = r.approved_similar_filtered.length > 0;
                          const hasCrossBatchStagingCandidate = r.staging_similar_filtered.some((m) => !m.is_same_batch);
                          const mergeNoTarget =
                            v === 'merge' &&
                            !rowState[r.staging_question_id]?.mergeTargetId &&
                            !hasApprovedCandidate &&
                            !hasCrossBatchStagingCandidate;
                          // Reason shown via native title attr (works on disabled <button>)
                          const disabledReason = mergeNoTarget
                            ? 'Merge needs either an approved DB match OR a cross-file staging match above the threshold. Same-batch staging matches are de-duped at upload time, not here.'
                            : undefined;
                          return (
                            <Button
                              key={v}
                              size="sm"
                              variant={action === v ? 'default' : 'outline'}
                              className={`h-7 px-2 text-[11px] justify-start ${action === v ? '' : cls}`}
                              onClick={() => setAction(
                                r.staging_question_id,
                                v,
                                v === 'merge' ? rowState[r.staging_question_id]?.mergeTargetId : null,
                                v === 'merge' ? rowState[r.staging_question_id]?.mergeTargetSource : null
                              )}
                              disabled={mergeNoTarget}
                              title={disabledReason}
                              data-testid={`row-action-${v}-${r.staging_question_id}`}
                            >
                              <Icon className="w-3 h-3 mr-1" /> {label}
                            </Button>
                          );
                        })}
                        {action === 'merge' && !rowState[r.staging_question_id]?.mergeTargetId && (
                          <div className="text-[10px] text-amber-700 mt-1 flex items-start gap-1">
                            <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                            <span>Pick a match above</span>
                          </div>
                        )}
                        {/* Inline micro-explainer when Merge is grayed out due to no mergeable candidate */}
                        {r.approved_similar_filtered.length === 0
                          && !r.staging_similar_filtered.some((m) => !m.is_same_batch)
                          && !rowState[r.staging_question_id]?.mergeTargetId && (
                          <div
                            className="text-[10px] text-gray-400 mt-1 leading-tight"
                            data-testid={`merge-gray-reason-${r.staging_question_id}`}
                          >
                            Merge needs an approved DB match or a cross-file staging match above the threshold.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {filteredRows.length > ROWS_PER_PAGE && (
              <div className="flex justify-center mt-2">
                <Pagination
                  currentPage={page}
                  totalItems={filteredRows.length}
                  itemsPerPage={ROWS_PER_PAGE}
                  onPageChange={setPage}
                  onItemsPerPageChange={() => {}}
                />
              </div>
            )}

            {/* Footer summary */}
            <DialogFooter className="border-t pt-3 mt-2 flex items-center justify-between gap-2 sm:justify-between">
              <div className="text-xs text-gray-600 mr-auto">
                <span className="text-gray-700 font-medium">{summary.keep}</span> Keep · {' '}
                <span className="text-emerald-700 font-medium">{summary.merge}</span> Merge · {' '}
                <span className="text-red-700 font-medium">{summary.skip}</span> Delete · {' '}
                <span className="text-amber-700 font-medium">{summary.defer}</span> Defer
                <span className="text-gray-400 ml-2">
                  · Keep & Defer are bookmarks only, no DB effect
                </span>
              </div>
              <Button variant="outline" onClick={onClose} disabled={applying}
                data-testid="bulk-sim-cancel-btn">
                Close
              </Button>
              <Button
                onClick={handleApply}
                disabled={applying || (summary.skip + summary.merge === 0)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="bulk-sim-apply-btn"
              >
                {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Apply ({summary.skip + summary.merge})
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BulkSimilarityModal;
